import React from "react";
import SEO from "../components/SEO";

const About = () => {
  return (
    <>
      <SEO
        title="About"
        description="Patch Designers For Hats"
        keywords="Patch Designers For Hats"
      />

      <div className="min-h-screen bg-gradient-to-b from-[#eef6ff] via-white to-[#fff4e8]">
        <div className="mx-auto w-full max-w-[1600px] px-4 py-10 sm:px-6 sm:py-12 lg:px-10 lg:py-14">
          {/* Hero Section */}
          <div className="mx-auto mb-10 max-w-[1000px] text-center sm:mb-12 lg:mb-14">
            <span className="mb-5 inline-flex items-center justify-center rounded-full border border-[#f97316]/20 bg-[#f97316]/10 px-5 py-2 text-[13px] font-bold uppercase tracking-[1px] text-[#f97316] shadow-sm sm:text-[14px]">
              Premium Custom Hat Patches
            </span>

            <h1 className="text-[34px] font-extrabold leading-tight tracking-wide text-[#2b2b2b] sm:text-[46px] lg:text-[58px]">
              About Personalised Patches
              <span className="mt-2 block text-[26px] font-bold text-[#0a2336]/75 sm:text-[34px] lg:text-[42px]">
                for Hats
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-[820px] text-[15px] font-medium leading-[28px] text-[#415164] sm:text-[17px] lg:text-[18px] lg:leading-[31px]">
              Your trusted partner for premium, durable, and beautifully crafted
              custom patches designed specifically for hats & caps.
            </p>
          </div>

          {/* Main Card */}
          <div className="rounded-[26px] border border-[#e5eaf0] bg-white/90 px-4 py-8 shadow-[0_18px_55px_rgba(15,35,54,0.10)] sm:px-6 sm:py-10 lg:px-10 lg:py-12">
            <div className="space-y-8 sm:space-y-10 lg:space-y-12">
              {/* Intro */}
              <section className="rounded-[22px] border border-[#e5eaf0] bg-gradient-to-br from-[#f8fafc] via-white to-[#fff7ed] px-5 py-8 shadow-sm sm:px-8 lg:px-10">
                <div className="max-w-[980px]">
                  <h2 className="text-[28px] font-bold leading-tight tracking-wide text-[#2b2b2b] sm:text-[34px] lg:text-[40px]">
                    The Patch Partner Customers Trust
                  </h2>

                  <div className="mt-5 h-px w-full max-w-[520px] bg-[#9ca3af]"></div>

                  <p className="mt-6 max-w-4xl text-[15px] font-medium leading-[28px] text-[#415164] sm:text-[17px] lg:text-[18px] lg:leading-[32px]">
                    At{" "}
                    <strong className="font-extrabold text-[#0a2336]">
                      Personalised Patches for Hats
                    </strong>
                    , we believe creating custom hat patches should be
                    effortless, fast, and consistently high-quality. Whether
                    you’re building a brand, outfitting a team, or launching
                    merchandise, we simplify the entire process — from design to
                    delivery.
                  </p>
                </div>
              </section>

              {/* Services Grid */}
              <section className="rounded-[22px] border border-[#e5eaf0] bg-white px-5 py-8 shadow-sm sm:px-8 lg:px-10">
                <div className="mb-8 text-center sm:mb-10">
                  <h2 className="text-center text-3xl font-bold tracking-wide text-[#2b2b2b] md:text-4xl">
                    What We Specialize In
                  </h2>

                  <div className="mx-auto mt-6 h-px w-full max-w-[620px] bg-[#9ca3af]"></div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {[
                    "Embroidered, Woven, PVC, Chenille, Leather & Bullion Patches",
                    "Custom Shapes, Premium Finishes & Full-Color Designs",
                    "Patches Engineered Specifically for Hats & Caps",
                    "Easy Online Ordering in Minutes",
                    "Low Minimums & Bulk Discounts",
                    "Fast Turnaround with Rush Options",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="group rounded-[18px] border border-[#e5eaf0] bg-[#f8fafc] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#f97316]/40 hover:bg-white hover:shadow-[0_16px_35px_rgba(15,35,54,0.10)]"
                    >
                      <div className="flex items-start gap-4">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f97316]/10 text-[16px] font-extrabold text-[#f97316] transition-all duration-300 group-hover:bg-[#f97316] group-hover:text-white">
                          ✔
                        </span>

                        <p className="text-[15px] font-semibold leading-[25px] text-[#0a2336] sm:text-[16px]">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Why Choose Us */}
              <section className="overflow-hidden rounded-[24px] bg-[rgba(31,44,76,0.95)] px-5 py-8 text-white shadow-[0_20px_50px_rgba(15,35,54,0.20)] sm:px-8 lg:px-10 lg:py-10">
                <div className="mb-8">
                  <h2 className="text-[28px] font-bold leading-tight tracking-wide text-white sm:text-[34px] lg:text-[40px]">
                    What Sets Us Apart
                  </h2>

                  <div className="mt-5 h-px w-full max-w-[520px] bg-white/35"></div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    "⚡ Instant pricing — no waiting for quotes",
                    "🚀 Fast production & reliable delivery",
                    "🤝 Real human support — no bots",
                    "🎯 Precision craftsmanship & strict quality control",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="rounded-[16px] border border-white/15 bg-white/10 px-5 py-4 text-[15px] font-semibold leading-[25px] text-white shadow-sm sm:text-[17px]"
                    >
                      {item}
                    </div>
                  ))}
                </div>

                <p className="mt-8 max-w-3xl text-[15px] font-medium leading-[27px] text-white/80 sm:text-[16px]">
                  We’re more than patch manufacturers — we help brands,
                  creators, and organizations bring their identity to life with
                  confidence.
                </p>
              </section>

              {/* Trust & Authority */}
              <section className="rounded-[22px] border border-[#e5eaf0] bg-gradient-to-br from-[#f8fafc] via-white to-[#fff7ed] px-5 py-8 shadow-sm sm:px-8 lg:px-10">
                <div className="mb-8 text-center sm:mb-10">
                  <h2 className="text-center text-3xl font-bold tracking-wide text-[#2b2b2b] md:text-4xl">
                    Authority, Experience & Trust
                  </h2>

                  <div className="mx-auto mt-6 h-px w-full max-w-[620px] bg-[#9ca3af]"></div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  {[
                    {
                      title: "Decades of Experience",
                      desc: "Serving businesses, brands & organizations across the USA.",
                    },
                    {
                      title: "Premium Quality Control",
                      desc: "Durable stitching, strong adhesion & flawless detailing.",
                    },
                    {
                      title: "Satisfaction Guaranteed",
                      desc: "Transparent communication & customer-first service.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="group rounded-[18px] border border-[#e5eaf0] bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#f97316]/40 hover:shadow-[0_18px_40px_rgba(15,35,54,0.12)] sm:p-7"
                    >
                      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#0a2336] text-[18px] font-extrabold text-white transition-all duration-300 group-hover:bg-[#f97316]">
                        {String(index + 1).padStart(2, "0")}
                      </div>

                      <h3 className="mb-3 text-[18px] font-extrabold leading-[25px] text-[#0a2336]">
                        {item.title}
                      </h3>

                      <p className="text-[14px] font-medium leading-[24px] text-[#415164] sm:text-[15px]">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Media Division */}
              <section className="rounded-[22px] border border-[#e5eaf0] bg-white px-5 py-8 shadow-sm sm:px-8 lg:px-10">
                <div className="max-w-[980px]">
                  <h2 className="text-[28px] font-bold leading-tight tracking-wide text-[#2b2b2b] sm:text-[34px] lg:text-[40px]">
                    Our Media & Publication Division
                  </h2>

                  <div className="mt-5 h-px w-full max-w-[520px] bg-[#9ca3af]"></div>

                  <p className="mt-6 max-w-4xl text-[15px] font-medium leading-[28px] text-[#415164] sm:text-[17px] lg:text-[18px] lg:leading-[32px]">
                    To share our expertise beyond manufacturing, we launched{" "}
                    <strong className="font-extrabold text-[#0a2336]">
                      EmbroiderCraft Studio — Powered by Personalised Patches
                      for Hats
                    </strong>
                    . Through in-depth tutorials, guides, and articles, we help
                    embroidery professionals, hobbyists, and businesses master
                    the art of patch creation.
                  </p>

                  <p className="mt-5 text-[15px] font-semibold leading-[26px] text-[#415164] sm:text-[16px]">
                    Our mission is simple: make professional embroidery
                    knowledge accessible to everyone.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;